/**
 * Configuration slice - handles return center settings
 * Maps to ConfigurationController endpoints
 */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { companyChanged } from 'store/actions/companyActions';
import type { RootState } from 'store';
import axios from 'axios';
import { configService } from 'services/configService';
import { ReturnCenterSettings, ReturnCenterAdditionalCost, ErpSource } from 'returnCenter-api';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import type { ThunkConf } from 'store/thunks';

interface ConfigState {
    settings: ReturnCenterSettings | null;
    settingsLoading: boolean;
    settingsError: string | null;
}

const initialState: ConfigState = {
    settings: null,
    settingsLoading: false,
    settingsError: null,
};

// ============ THUNKS ============

export const getReturnCenterSettings = createAsyncThunk<ReturnCenterSettings, void, ThunkConf>(
    'config/getSettings',
    async (_, { rejectWithValue }) => {
        try {
            return await configService.getReturnCenterSettings();
        } catch (error) {
            console.error('Error fetching return center settings:', error);
            if (axios.isCancel(error)) return rejectWithValue(undefined);
            toast.warning(t('settings.loadError', 'Failed to load settings. Some features may be unavailable.'), {
                autoClose: 5000,
                closeOnClick: true,
                toastId: 'settings-error',
            });
            return rejectWithValue({ message: t('settings.loadError', 'Failed to load return center settings') });
        }
    },
);

// ============ SLICE ============

const configSlice = createSlice({
    name: 'config',
    initialState,
    reducers: {
        resetConfigState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getReturnCenterSettings.pending, (state) => {
                state.settingsLoading = true;
                state.settingsError = null;
            })
            .addCase(getReturnCenterSettings.fulfilled, (state, action) => {
                state.settingsLoading = false;
                state.settings = action.payload;
            })
            .addCase(getReturnCenterSettings.rejected, (state, action) => {
                state.settingsLoading = false;
                state.settingsError = (action.payload as any)?.message ?? 'Failed to load settings';
            });

        // Reset on company change
        builder.addCase(companyChanged, () => initialState);
    },
});

// ============ SELECTORS ============

export const selectSettings = (state: RootState) => state.config.settings;
export const selectSettingsLoading = (state: RootState) => state.config.settingsLoading;
export const selectSettingsError = (state: RootState) => state.config.settingsError;
export const selectReasonCodes = createSelector(selectSettings, (settings) => settings?.reasonCodes);
const selectIsM3Company = createSelector(
    selectSettings,
    (settings) => settings?.erpSource === ErpSource.M3Prod || settings?.erpSource === ErpSource.M3Dev,
);

export const selectReasonCodesMap = createSelector(
    selectReasonCodes,
    (reasonCodes) => reasonCodes?.map((reason) => ({ label: reason.name, value: reason.id })) ?? [],
);

export const selectAdditionalCostOptions = createSelector(selectSettings, (settings) =>
    (settings?.returnCenterAdditionalCosts ?? [])
        .map((cost: ReturnCenterAdditionalCost) => ({
            label: cost.description,
            value: cost.id,
            data: cost,
            key: cost.id,
        }))
        .filter(Boolean),
);

export const selectHasAdditionalCostOptions = createSelector(
    selectAdditionalCostOptions,
    (options) => options.length > 0,
);

export const selectSelectedAdditionalCostOption = createSelector(
    [
        selectAdditionalCostOptions,
        (_: RootState, selectedCostOption: ReturnCenterAdditionalCost | string | null) => selectedCostOption,
    ],
    (options, selectedCostOption) => {
        if (!selectedCostOption) return null;
        const id = typeof selectedCostOption === 'string' ? selectedCostOption : selectedCostOption.id;
        return options.find((option) => option.data.id === id)?.data ?? null;
    },
);

export const selectSettingsAvailable = createSelector(
    [selectSettings, selectSettingsError, selectSettingsLoading],
    (settings, error, loading) => ({
        isAvailable: settings !== null && !error,
        isLoading: loading,
        hasError: !!error,
        hasReasonCodes: (settings?.reasonCodes?.length ?? 0) > 0,
        hasAdditionalCosts: (settings?.returnCenterAdditionalCosts?.length ?? 0) > 0,
    }),
);

export const selectCreditLabelOptions = createSelector(selectSettings, (settings) =>
    (settings?.creditLabels ?? [])
        .map((label) => ({
            label: label.name,
            value: label.value,
        }))
        .filter(Boolean),
);

// ============ EXPORTS ============

export const configActions = configSlice.actions;
export const { resetConfigState } = configSlice.actions;

export const configThunks = {
    getReturnCenterSettings,
};

export const configSelectors = {
    selectSettings,
    selectSettingsLoading,
    selectSettingsError,
    selectReasonCodes,
    selectReasonCodesMap,
    selectAdditionalCostOptions,
    selectHasAdditionalCostOptions,
    selectSelectedAdditionalCostOption,
    selectSettingsAvailable,
    selectCreditLabelOptions,
    selectIsM3Company,
};

export default configSlice.reducer;
